import { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import usePermissions from '../../hooks/usePermissions';
import './ProductPacks.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

/* ── Pack Modal ─────────────────────────────────────────────────── */
const PackModal = ({ initial, onSave, onClose }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(
    initial ? { ...initial } :
    { name: '', description: '', image_url: '', multiples_of: 1, is_active: true, display_order: 0 }
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const sf = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const isServerPackImage = (url) => url && url.startsWith('/uploads/pack-images/');

  const deleteServerPackImage = async (url) => {
    if (!isServerPackImage(url)) return;
    try {
      await axios.delete(`${API_URL}/pack-admin/packs/image`, {
        ...auth(), data: { url },
      });
    } catch {
      console.warn('Could not delete old pack image from server:', url);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setError('');
    try {
      if (isServerPackImage(form.image_url)) {
        await deleteServerPackImage(form.image_url);
      }
      const data = new FormData();
      data.append('image', file);
      const res = await axios.post(`${API_URL}/pack-admin/packs/upload-image`, data, {
        headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' },
      });
      sf('image_url', res.data.url);
    } catch (err) {
      setError(err.response?.data?.error || 'Image upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError('');
    try {
      if (isEdit) await axios.put(`${API_URL}/pack-admin/packs/${initial.id}`, form, auth());
      else        await axios.post(`${API_URL}/pack-admin/packs`, form, auth());
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save.');
    } finally { setSaving(false); }
  };

  return (
    <div className="pp-overlay" onClick={onClose}>
      <div className="pp-modal" onClick={e => e.stopPropagation()}>
        <div className="pp-modal-header">
          <h2>{isEdit ? 'Edit Pack' : 'Add Pack'}</h2>
          <button className="pp-modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="pp-alert pp-alert--error">{error}</div>}
          <div className="pp-form-row">
            <div className="pp-form-group pp-form-group--grow">
              <label>Name *</label>
              <input className="pp-input" value={form.name} onChange={e => sf('name', e.target.value)} required />
            </div>
            <div className="pp-form-group">
              <label>Multiples Of</label>
              <input className="pp-input" type="number" min="1" value={form.multiples_of}
                onChange={e => sf('multiples_of', parseInt(e.target.value) || 1)} />
            </div>
          </div>
          <div className="pp-form-group">
            <label>Description</label>
            <textarea className="pp-input pp-textarea" rows={2} value={form.description || ''}
              onChange={e => sf('description', e.target.value)} />
          </div>
          <div className="pp-form-group">
            <label>Pack Image</label>
            <div className="img-upload-area">
              {resolveImg(form.image_url) && (
                <div className="img-preview-wrap">
                  <img src={resolveImg(form.image_url)} alt="preview" className="img-preview" />
                  <button type="button" className="img-clear-btn" onClick={async () => { await deleteServerPackImage(form.image_url); sf('image_url', ''); }} title="Remove image">×</button>
                </div>
              )}
              <label className={`img-upload-btn${uploading ? ' img-upload-btn--loading' : ''}`}>
                {uploading ? 'Uploading…' : resolveImg(form.image_url) ? '🔄 Replace Image' : '📷 Upload Image'}
                <input type="file" accept="image/jpeg,image/png,image/gif,image/webp"
                  style={{ display: 'none' }} onChange={handleImageChange} disabled={uploading} />
              </label>
            </div>
          </div>
          <div className="pp-form-row">
            <div className="pp-form-group">
              <label>Display Order</label>
              <input className="pp-input" type="number" value={form.display_order}
                onChange={e => sf('display_order', parseInt(e.target.value) || 0)} />
            </div>
            <div className="pp-form-group pp-form-group--center">
              <label className="pp-checkbox-label">
                <input type="checkbox" checked={form.is_active} onChange={e => sf('is_active', e.target.checked)} />
                Active
              </label>
            </div>
          </div>
          <div className="pp-modal-footer">
            <button type="button" className="pp-btn pp-btn--cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="pp-btn pp-btn--primary" disabled={saving || uploading}>
              {saving ? 'Saving…' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Group Modal ────────────────────────────────────────────────── */
const GroupModal = ({ initial, onSave, onClose }) => {
  const isEdit = !!initial?.id;
  const [name, setName]   = useState(initial?.name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError('');
    try {
      if (isEdit) await axios.put(`${API_URL}/pack-admin/groups/${initial.id}`, { name: name.trim() }, auth());
      else        await axios.post(`${API_URL}/pack-admin/groups`, { name: name.trim() }, auth());
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save.');
    } finally { setSaving(false); }
  };

  return (
    <div className="pp-overlay" onClick={onClose}>
      <div className="pp-modal" onClick={e => e.stopPropagation()}>
        <div className="pp-modal-header">
          <h2>{isEdit ? 'Edit Group' : 'Add Group'}</h2>
          <button className="pp-modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="pp-alert pp-alert--error">{error}</div>}
          <div className="pp-form-group">
            <label>Group Name *</label>
            <input className="pp-input" value={name} onChange={e => setName(e.target.value)} required />
          </div>
          <div className="pp-modal-footer">
            <button type="button" className="pp-btn pp-btn--cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="pp-btn pp-btn--primary" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Group Product Modal ────────────────────────────────────────── */
const GroupProductModal = ({ groupId, existingIds, productsLov, initial, onSave, onClose }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(
    initial
      ? { product_id: initial.product_id, is_active: initial.is_active, display_order: initial.display_order }
      : { product_id: '', is_active: true, display_order: 0 }
  );
  const [search, setSearch] = useState(initial?.product_name || '');
  const [showDrop, setShowDrop] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dropRef = useRef(null);
  const sf = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    const handler = e => { if (dropRef.current && !dropRef.current.contains(e.target)) setShowDrop(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const available = productsLov.filter(p => !existingIds.includes(p.id) || p.id === form.product_id);
  const filtered  = search.trim()
    ? available.filter(p => p.name.toLowerCase().includes(search.toLowerCase()))
    : available;

  const handleSelect = (p) => { sf('product_id', p.id); setSearch(p.name); setShowDrop(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.product_id) { setError('Please select a product.'); return; }
    setSaving(true); setError('');
    try {
      if (isEdit) {
        await axios.put(`${API_URL}/pack-admin/group-products/${initial.id}`,
          { is_active: form.is_active, display_order: form.display_order }, auth());
      } else {
        await axios.post(`${API_URL}/pack-admin/groups/${groupId}/products`, form, auth());
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save.');
    } finally { setSaving(false); }
  };

  return (
    <div className="pp-overlay" onClick={onClose}>
      <div className="pp-modal pp-modal--product" onClick={e => e.stopPropagation()}>
        <div className="pp-modal-header">
          <h2>{isEdit ? 'Edit Product' : 'Add Product to Group'}</h2>
          <button className="pp-modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="pp-alert pp-alert--error">{error}</div>}
          <div className="pp-form-group" ref={dropRef}>
            <label>Product *</label>
            <input className="pp-input" value={search} disabled={isEdit}
              onChange={e => { setSearch(e.target.value); setShowDrop(true); if (!e.target.value) sf('product_id', ''); }}
              onFocus={() => setShowDrop(true)}
              placeholder="Type to search…" />
            {showDrop && filtered.length > 0 && (
              <ul className="pp-dropdown">
                {filtered.slice(0, 50).map(p => (
                  <li key={p.id} className="pp-dropdown-item" onMouseDown={() => handleSelect(p)}>{p.name}</li>
                ))}
              </ul>
            )}
          </div>
          <div className="pp-form-row">
            <div className="pp-form-group">
              <label>Display Order</label>
              <input className="pp-input" type="number" value={form.display_order}
                onChange={e => sf('display_order', parseInt(e.target.value) || 0)} />
            </div>
            <div className="pp-form-group pp-form-group--center">
              <label className="pp-checkbox-label">
                <input type="checkbox" checked={form.is_active} onChange={e => sf('is_active', e.target.checked)} />
                Active
              </label>
            </div>
          </div>
          <div className="pp-modal-footer">
            <button type="button" className="pp-btn pp-btn--cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="pp-btn pp-btn--primary" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Assign Group Modal ─────────────────────────────────────────── */
const AssignGroupModal = ({ packId, allGroups, assignedGroupIds, onSave, onClose }) => {
  const available = allGroups.filter(g => !assignedGroupIds.includes(g.id));
  const [groupId, setGroupId]           = useState('');
  const [displayOrder, setDisplayOrder] = useState(assignedGroupIds.length);
  const [maxUnique, setMaxUnique]       = useState('');
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!groupId) { setError('Please select a group.'); return; }
    setSaving(true); setError('');
    try {
      await axios.post(`${API_URL}/pack-admin/packs/${packId}/groups`, {
        group_id: parseInt(groupId),
        display_order: displayOrder,
        max_unique: maxUnique !== '' ? parseInt(maxUnique) : null,
      }, auth());
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to assign group.');
    } finally { setSaving(false); }
  };

  return (
    <div className="pp-overlay" onClick={onClose}>
      <div className="pp-modal" onClick={e => e.stopPropagation()}>
        <div className="pp-modal-header">
          <h2>Assign Group to Pack</h2>
          <button className="pp-modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="pp-alert pp-alert--error">{error}</div>}
          {available.length === 0
            ? <p style={{ padding: '12px 0', color: '#888', fontSize: 13 }}>All groups are already assigned to this pack.</p>
            : <>
                <div className="pp-form-group">
                  <label>Group *</label>
                  <select className="pp-input" value={groupId} onChange={e => setGroupId(e.target.value)} required>
                    <option value="">— select a group —</option>
                    {available.map(g => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                </div>
                <div className="pp-form-row">
                  <div className="pp-form-group">
                    <label>Display Order</label>
                    <input className="pp-input" type="number" value={displayOrder}
                      onChange={e => setDisplayOrder(parseInt(e.target.value) || 0)} />
                  </div>
                  <div className="pp-form-group">
                    <label>Max Unique <span className="pp-hint">(blank = no limit)</span></label>
                    <input className="pp-input" type="number" min="1" value={maxUnique}
                      onChange={e => setMaxUnique(e.target.value)} placeholder="—" />
                  </div>
                </div>
              </>
          }
          <div className="pp-modal-footer">
            <button type="button" className="pp-btn pp-btn--cancel" onClick={onClose}>Cancel</button>
            {available.length > 0 && (
              <button type="submit" className="pp-btn pp-btn--primary" disabled={saving}>
                {saving ? 'Saving…' : 'Assign'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Edit Assignment Modal ──────────────────────────────────────── */
const EditAssignmentModal = ({ assignment, onSave, onClose }) => {
  const [displayOrder, setDisplayOrder] = useState(assignment.display_order);
  const [maxUnique, setMaxUnique]       = useState(assignment.max_unique ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await axios.put(`${API_URL}/pack-admin/assignments/${assignment.assignment_id}`, {
        display_order: displayOrder,
        max_unique: maxUnique !== '' ? parseInt(maxUnique) : null,
      }, auth());
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update.');
    } finally { setSaving(false); }
  };

  return (
    <div className="pp-overlay" onClick={onClose}>
      <div className="pp-modal" onClick={e => e.stopPropagation()}>
        <div className="pp-modal-header">
          <h2>Edit — {assignment.name}</h2>
          <button className="pp-modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="pp-alert pp-alert--error">{error}</div>}
          <div className="pp-form-row">
            <div className="pp-form-group">
              <label>Display Order</label>
              <input className="pp-input" type="number" value={displayOrder}
                onChange={e => setDisplayOrder(parseInt(e.target.value) || 0)} />
            </div>
            <div className="pp-form-group">
              <label>Max Unique <span className="pp-hint">(blank = no limit)</span></label>
              <input className="pp-input" type="number" min="1" value={maxUnique}
                onChange={e => setMaxUnique(e.target.value)} placeholder="—" />
            </div>
          </div>
          <div className="pp-modal-footer">
            <button type="button" className="pp-btn pp-btn--cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="pp-btn pp-btn--primary" disabled={saving}>
              {saving ? 'Saving…' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Main Page ──────────────────────────────────────────────────── */
const ProductPacks = () => {
  const { hasPermission } = usePermissions();

  const [activeTab, setActiveTab] = useState('packs');

  // Groups tab
  const [groups, setGroups]             = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupProducts, setGroupProducts] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [gpLoading, setGpLoading]         = useState(false);

  // Packs tab
  const [packs, setPacks]               = useState([]);
  const [selectedPack, setSelectedPack] = useState(null);
  const [assignments, setAssignments]   = useState([]);
  const [packsLoading, setPacksLoading]         = useState(false);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);

  // Shared
  const [productsLov, setProductsLov] = useState([]);
  const [modal, setModal]   = useState(null);
  const [error, setError]   = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => { loadGroups(); loadPacks(); loadProductsLov(); }, []); // eslint-disable-line

  useEffect(() => {
    if (selectedGroup) loadGroupProducts(selectedGroup.id);
    else setGroupProducts([]);
  }, [selectedGroup]); // eslint-disable-line

  useEffect(() => {
    if (selectedPack) loadAssignments(selectedPack.id);
    else setAssignments([]);
  }, [selectedPack]); // eslint-disable-line

  if (!hasPermission('packs.read')) {
    return (
      <div className="pp-page">
        <div className="pp-access-denied">
          <div className="pp-access-denied-icon">🔒</div>
          <h2>Access Denied</h2>
          <p>You do not have permission to view Product Packs.</p>
        </div>
      </div>
    );
  }

  const loadGroups = async () => {
    setGroupsLoading(true);
    try { const r = await axios.get(`${API_URL}/pack-admin/groups`, auth()); setGroups(r.data); }
    catch { setError('Failed to load groups.'); }
    finally { setGroupsLoading(false); }
  };

  const loadGroupProducts = async (groupId) => {
    setGpLoading(true);
    try { const r = await axios.get(`${API_URL}/pack-admin/groups/${groupId}/products`, auth()); setGroupProducts(r.data); }
    catch { setError('Failed to load group products.'); }
    finally { setGpLoading(false); }
  };

  const loadPacks = async () => {
    setPacksLoading(true);
    try { const r = await axios.get(`${API_URL}/pack-admin/packs`, auth()); setPacks(r.data); }
    catch { setError('Failed to load packs.'); }
    finally { setPacksLoading(false); }
  };

  const loadAssignments = async (packId) => {
    setAssignmentsLoading(true);
    try { const r = await axios.get(`${API_URL}/pack-admin/packs/${packId}/groups`, auth()); setAssignments(r.data); }
    catch { setError('Failed to load assignments.'); }
    finally { setAssignmentsLoading(false); }
  };

  const loadProductsLov = async () => {
    try { const r = await axios.get(`${API_URL}/products`, auth()); setProductsLov(r.data || []); }
    catch {}
  };

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Delete group "${group.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/pack-admin/groups/${group.id}`, auth());
      if (selectedGroup?.id === group.id) setSelectedGroup(null);
      loadGroups();
      flash('Group deleted.');
    } catch (err) { setError(err.response?.data?.error || 'Delete failed.'); }
  };

  const handleDeleteGP = async (gp) => {
    if (!window.confirm(`Remove "${gp.product_name}" from this group?`)) return;
    try {
      await axios.delete(`${API_URL}/pack-admin/group-products/${gp.id}`, auth());
      loadGroupProducts(selectedGroup.id);
      loadGroups();
      flash('Product removed.');
    } catch (err) { setError(err.response?.data?.error || 'Delete failed.'); }
  };

  const handleDeletePack = async (pack) => {
    if (!window.confirm(`Delete pack "${pack.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/pack-admin/packs/${pack.id}`, auth());
      if (selectedPack?.id === pack.id) setSelectedPack(null);
      loadPacks();
      flash('Pack deleted.');
    } catch (err) { setError(err.response?.data?.error || 'Delete failed.'); }
  };

  const handleUnassign = async (assignment) => {
    if (!window.confirm(`Remove "${assignment.name}" from this pack?`)) return;
    try {
      await axios.delete(`${API_URL}/pack-admin/assignments/${assignment.assignment_id}`, auth());
      loadAssignments(selectedPack.id);
      loadPacks();
      flash('Group removed from pack.');
    } catch (err) { setError(err.response?.data?.error || 'Delete failed.'); }
  };

  const assignedGroupIds = assignments.map(a => a.group_id);
  const existingProductIds = groupProducts.map(gp => gp.product_id);

  return (
    <div className="pp-page">
      <div className="pp-page-header">
        <h1 className="pp-title">Product Packs</h1>
      </div>

      {error   && <div className="pp-alert pp-alert--error" onClick={() => setError('')}>{error}</div>}
      {success && <div className="pp-alert pp-alert--success">{success}</div>}

      {/* ── Tabs ── */}
      <div className="pp-tabs">
        <button className={`pp-tab${activeTab === 'packs' ? ' pp-tab--active' : ''}`}
          onClick={() => setActiveTab('packs')}>Packs</button>
        <button className={`pp-tab${activeTab === 'groups' ? ' pp-tab--active' : ''}`}
          onClick={() => setActiveTab('groups')}>Groups</button>
      </div>

      {/* ── Groups Tab ── */}
      {activeTab === 'groups' && (
        <div className="pp-panels">
          {/* Column 1: All groups */}
          <div className="pp-panel">
            <div className="pp-panel-header">
              <span>Groups</span>
              <button className="pp-btn pp-btn--add" onClick={() => setModal({ type: 'group' })}>+ Add</button>
            </div>
            <div className="pp-panel-body">
              {groupsLoading && <div className="pp-spinner" />}
              {!groupsLoading && groups.length === 0 && <p className="pp-empty">No groups yet.</p>}
              {groups.map(g => (
                <div key={g.id}
                  className={`pp-row${selectedGroup?.id === g.id ? ' pp-row--selected' : ''}`}
                  onClick={() => setSelectedGroup(g)}>
                  <div className="pp-row-info">
                    <span className="pp-row-name">{g.name}</span>
                    <span className="pp-row-meta">
                      {g.product_count} product{g.product_count !== 1 ? 's' : ''}
                      &nbsp;·&nbsp;{g.pack_count} pack{g.pack_count !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="pp-row-actions">
                    <button className="pp-icon-btn" title="Edit"
                      onClick={e => { e.stopPropagation(); setModal({ type: 'group', initial: g }); }}>✏️</button>
                    <button className="pp-icon-btn pp-icon-btn--del" title="Delete"
                      onClick={e => { e.stopPropagation(); handleDeleteGroup(g); }}>🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Column 2: Products for selected group */}
          <div className={`pp-panel${!selectedGroup ? ' pp-panel--dim' : ''}`}>
            <div className="pp-panel-header">
              <span>{selectedGroup ? `Products — ${selectedGroup.name}` : 'Products'}</span>
              {selectedGroup && (
                <button className="pp-btn pp-btn--add" onClick={() => setModal({ type: 'group-product' })}>+ Add</button>
              )}
            </div>
            <div className="pp-panel-body">
              {!selectedGroup && <p className="pp-empty">Select a group to see its products.</p>}
              {selectedGroup && gpLoading && <div className="pp-spinner" />}
              {selectedGroup && !gpLoading && groupProducts.length === 0 && <p className="pp-empty">No products yet.</p>}
              {groupProducts.map(gp => (
                <div key={gp.id} className={`pp-row${!gp.is_active ? ' pp-row--inactive' : ''}`}>
                  <div className="pp-row-info">
                    <span className="pp-row-name">{gp.product_name}</span>
                    <span className="pp-row-meta">order {gp.display_order}{!gp.is_active ? ' · inactive' : ''}</span>
                  </div>
                  <div className="pp-row-actions">
                    <button className="pp-icon-btn" title="Edit"
                      onClick={() => setModal({ type: 'group-product', initial: gp })}>✏️</button>
                    <button className="pp-icon-btn pp-icon-btn--del" title="Remove"
                      onClick={() => handleDeleteGP(gp)}>🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Packs Tab ── */}
      {activeTab === 'packs' && (
        <div className="pp-panels">
          {/* Column 1: All packs */}
          <div className="pp-panel">
            <div className="pp-panel-header">
              <span>Packs</span>
              <button className="pp-btn pp-btn--add" onClick={() => setModal({ type: 'pack' })}>+ Add</button>
            </div>
            <div className="pp-panel-body">
              {packsLoading && <div className="pp-spinner" />}
              {!packsLoading && packs.length === 0 && <p className="pp-empty">No packs yet.</p>}
              {packs.map(pack => (
                <div key={pack.id}
                  className={`pp-row${selectedPack?.id === pack.id ? ' pp-row--selected' : ''}${!pack.is_active ? ' pp-row--inactive' : ''}`}
                  onClick={() => setSelectedPack(pack)}>
                  <div className="pp-row-info">
                    <span className="pp-row-name">{pack.name}</span>
                    <span className="pp-row-meta">
                      ×{pack.multiples_of}
                      &nbsp;·&nbsp;{pack.group_count} group{pack.group_count !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="pp-row-actions">
                    <button className="pp-icon-btn" title="Edit"
                      onClick={e => { e.stopPropagation(); setModal({ type: 'pack', initial: pack }); }}>✏️</button>
                    <button className="pp-icon-btn pp-icon-btn--del" title="Delete"
                      onClick={e => { e.stopPropagation(); handleDeletePack(pack); }}>🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Column 2: Groups assigned to selected pack */}
          <div className={`pp-panel${!selectedPack ? ' pp-panel--dim' : ''}`}>
            <div className="pp-panel-header">
              <span>{selectedPack ? `Groups — ${selectedPack.name}` : 'Groups'}</span>
              {selectedPack && (
                <button className="pp-btn pp-btn--add" onClick={() => setModal({ type: 'assign-group' })}>+ Assign</button>
              )}
            </div>
            <div className="pp-panel-body">
              {!selectedPack && <p className="pp-empty">Select a pack to manage its groups.</p>}
              {selectedPack && assignmentsLoading && <div className="pp-spinner" />}
              {selectedPack && !assignmentsLoading && assignments.length === 0 && <p className="pp-empty">No groups assigned yet.</p>}
              {assignments.map(a => (
                <div key={a.assignment_id} className="pp-row">
                  <div className="pp-row-info">
                    <span className="pp-row-name">{a.name}</span>
                    <span className="pp-row-meta">
                      order {a.display_order}
                      &nbsp;·&nbsp;{a.max_unique ? `max ${a.max_unique} unique` : 'no unique limit'}
                      &nbsp;·&nbsp;{a.product_count} product{a.product_count !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="pp-row-actions">
                    <button className="pp-icon-btn" title="Edit order"
                      onClick={() => setModal({ type: 'edit-assignment', initial: a })}>✏️</button>
                    <button className="pp-icon-btn pp-icon-btn--del" title="Remove"
                      onClick={() => handleUnassign(a)}>🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      {modal?.type === 'pack' && (
        <PackModal
          initial={modal.initial || null}
          onSave={() => { setModal(null); loadPacks(); flash(modal.initial ? 'Pack updated.' : 'Pack created.'); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'group' && (
        <GroupModal
          initial={modal.initial || null}
          onSave={() => { setModal(null); loadGroups(); flash(modal.initial ? 'Group updated.' : 'Group created.'); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'group-product' && selectedGroup && (
        <GroupProductModal
          groupId={selectedGroup.id}
          existingIds={existingProductIds}
          productsLov={productsLov}
          initial={modal.initial || null}
          onSave={() => { setModal(null); loadGroupProducts(selectedGroup.id); loadGroups(); flash(modal.initial ? 'Product updated.' : 'Product added.'); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'assign-group' && selectedPack && (
        <AssignGroupModal
          packId={selectedPack.id}
          allGroups={groups}
          assignedGroupIds={assignedGroupIds}
          onSave={() => { setModal(null); loadAssignments(selectedPack.id); loadPacks(); flash('Group assigned.'); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'edit-assignment' && (
        <EditAssignmentModal
          assignment={modal.initial}
          onSave={() => { setModal(null); loadAssignments(selectedPack.id); flash('Order updated.'); }}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default ProductPacks;
